import React, { RefObject } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { EditButton } from "@refinedev/mui";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import HttpIcon from "@mui/icons-material/Http";

import { TableCell, ModalRef } from "@shared/components";
import { masks } from "@core/utils/masks";
import { AgreementIntegrationConfig } from "@modules/erpIntegrations/types";
import { CopyTextButton } from "@modules/erpIntegrations/components/CopyTextButton";

function TestDocumentCell({ value }: { value?: string }) {
  const digits = (value ?? "").replace(/\D/g, "");
  if (!digits) return <TableCell.Text value="—" textAlign="left" />;

  const masked = masks.document(digits);

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: "100%", minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {masked}
      </Typography>
      <Box onClick={(event) => event.stopPropagation()}>
        <CopyTextButton text={masked} />
      </Box>
    </Stack>
  );
}

export const useConfigsColumns = (
  translate: (key: string) => string,
  editModalRef: RefObject<ModalRef>,
  onDelete: (id: number) => void,
  onTest: (row: AgreementIntegrationConfig) => void,
  agreementNameById: Map<number, string>
): GridColDef[] => {
  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: translate("v1/agreement_integration_config_admin.fields.id"),
        width: 80,
        sortable: false,
      },
      {
        field: "agreement",
        headerName: translate(
          "v1/agreement_integration_config_admin.fields.agreement"
        ),
        minWidth: 220,
        flex: 1,
        renderCell: ({ value }) => {
          const name = agreementNameById.get(value);
          return (
            <TableCell.Text
              value={name ? `#${value} — ${name}` : `#${value}`}
              textAlign="left"
            />
          );
        },
      },
      {
        field: "integration",
        headerName: translate(
          "v1/agreement_integration_config_admin.fields.integration"
        ),
        minWidth: 180,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ row }) => (
          <TableCell.Text
            value={(row as AgreementIntegrationConfig).integration_detail?.name}
            textAlign="left"
          />
        ),
      },
      {
        field: "plan_map_option",
        headerName: translate(
          "v1/agreement_integration_config_admin.fields.plan_map_option_column"
        ),
        minWidth: 180,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ row }) => (
          <TableCell.Text
            value={
              (row as AgreementIntegrationConfig).plan_map_option_detail?.label
            }
            textAlign="left"
          />
        ),
      },
      {
        field: "test_document",
        headerName: translate(
          "v1/agreement_integration_config_admin.fields.test_document"
        ),
        minWidth: 200,
        sortable: false,
        headerAlign: "left",
        renderCell: ({ value }) => <TestDocumentCell value={value} />,
      },
      {
        field: "active",
        headerName: translate(
          "v1/agreement_integration_config_admin.fields.active"
        ),
        minWidth: 100,
        sortable: false,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Text value={value ? "Sim" : "Não"} textAlign="center" />
        ),
      },
      {
        field: "created_at",
        headerName: translate(
          "v1/agreement_integration_config_admin.fields.created_at"
        ),
        minWidth: 160,
        renderCell: ({ value }) => <TableCell.Date value={value} />,
      },
      {
        field: "updated_at",
        headerName: translate(
          "v1/agreement_integration_config_admin.fields.updated_at"
        ),
        minWidth: 160,
        renderCell: ({ value }) => <TableCell.Date value={value} />,
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        minWidth: 180,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            <Tooltip title={translate("erp_integrations.actions.test")}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => onTest(row as AgreementIntegrationConfig)}
                sx={{
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderRadius: 1,
                }}
              >
                <HttpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <EditButton
              hideText
              onClick={() => editModalRef.current?.open(row)}
            />
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell.Container>
        ),
      },
    ],
    [translate, editModalRef, onDelete, onTest, agreementNameById]
  );
};
